/**
 * productService.js — Products + Dashboard + QR API calls
 * =========================================================
 */

import axios from 'axios';
import httpClient from './httpClient';

// ---------------------------------------------------------------------------
// Django CSRF helper
// ---------------------------------------------------------------------------
function getCsrfToken() {
  const name = 'csrftoken';
  const cookies = document.cookie.split(';');
  for (let c of cookies) {
    const trimmed = c.trim();
    if (trimmed.startsWith(name + '=')) {
      return decodeURIComponent(trimmed.slice(name.length + 1));
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Public client — no session cookie, no 401→login interceptor
// Used for endpoints with AllowAny that must work for unauthenticated clients
// ---------------------------------------------------------------------------
const publicHttpClient = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 15000,
});

// Attach CSRF token to public client as well, because if a user is logged in, 
// Django will enforce CSRF even on AllowAny endpoints.
publicHttpClient.interceptors.request.use((config) => {
  const safeMethods = ['GET', 'HEAD', 'OPTIONS', 'TRACE'];
  if (!safeMethods.includes(config.method?.toUpperCase())) {
    const csrf = getCsrfToken();
    if (csrf) {
      config.headers['X-CSRFToken'] = csrf;
    }
  }
  return config;
});

// La ruta de la campaña Mapa SEO en el panel; Mapa Google Business cuelga de
// ella (`/google-business`). Ver `adminService.prospeccion*`.
export const PROSPECCION_BASE = '/mgmt/prospeccion';

export const productService = {
  /** List current user's products. */
  list: async () => {
    const { data } = await httpClient.get('/products/');
    return data;
  },

  /** Get a specific product by its Mongo _id. */
  get: async (productId) => {
    const { data } = await httpClient.get(`/products/${productId}/`);
    return data;
  },

  /** Update product fields. */
  update: async (productId, fields) => {
    const { data } = await httpClient.patch(`/products/${productId}/`, fields);
    return data;
  },

  /** Get analytics stats for a product. */
  stats: async (productId) => {
    const { data } = await httpClient.get(`/products/${productId}/stats/`);
    return data;
  },

  /** Claim ownership of an unregistered product. */
  claim: async (qrId) => {
    const { data } = await httpClient.post('/products/claim/', { qrId });
    return data;
  },
};

export const dashboardService = {
  /** Global KPI summary. */
  summary: async () => {
    const { data } = await httpClient.get('/dashboard/summary/');
    return data;
  },

  /** Views-per-day chart series. */
  chart: async (days = 30) => {
    const { data } = await httpClient.get('/dashboard/chart/', { params: { days } });
    return data;
  },

  /** Device type breakdown. */
  devices: async () => {
    const { data } = await httpClient.get('/dashboard/devices/');
    return data;
  },
  
  /** Location breakdown across all products. */
  locations: async () => {
    const { data } = await httpClient.get('/dashboard/locations/');
    return data;
  },
};

export const qrService = {
  /** Log a QR scan and get the redirect URL. */
  scan: async ({ qrId, country = '', city = '' }) => {
    const { data } = await publicHttpClient.post('/qr/scan/', { qrId, country, city });
    return data;
  },

  /** Check if a QR device is available for registration. */
  check: async (qrId) => {
    const { data } = await httpClient.get(`/qr/check/${qrId}/`);
    return data;
  },

  /** Get public config for Todo en 1 landing (no auth required). */
  getPublicConfig: async (qrId) => {
    const { data } = await publicHttpClient.get(`/qr/todo-en-1/${qrId}/`);
    return data;
  },
};

export const adminService = {
  /**
   * TEMPORAL (decisiones §152): episodios de la protección de ficha apuntados en
   * dry_run que siguen abiertos. Solo victor@mytapstar.com; borrar al pasar a live.
   */
  protectionReview: async () => {
    const { data } = await httpClient.get('/mgmt/protection-review/');
    return data;
  },

  /** TEMPORAL (§152): cierra esos episodios sin tocar Google. */
  discardProtectionEpisodes: async (ids) => {
    const { data } = await httpClient.post('/mgmt/protection-review/', { ids });
    return data;
  },

  /**
   * Alertas operativas del panel (Admin only). El backend recorre su registro de
   * proveedores, así que añadir un tipo de alerta no toca esta función.
   * @param {{countsOnly?: boolean}} opts `countsOnly` pide solo los totales —
   *   es lo que usa el contador del menú, y evita traerse datos de contacto de
   *   clientes a una vista que solo pinta un número.
   */
  alerts: async ({ countsOnly = false } = {}) => {
    const { data } = await httpClient.get('/mgmt/alerts/', {
      params: countsOnly ? { counts_only: 'true' } : {},
    });
    return {
      total: data?.total || 0,
      // Solo las alertas de severidad alta: es lo que pinta el badge del menú.
      // Ver la nota de `severity` en `QR/api/admin_alerts_views.py`.
      badgeTotal: data?.badgeTotal || 0,
      alerts: Array.isArray(data?.alerts) ? data.alerts : [],
      // Filas que un admin pospuso. Solo vienen con el detalle.
      snoozed: Array.isArray(data?.snoozed) ? data.snoozed : [],
    };
  },

  /**
   * Posponer una fila de alertas («Contactado»). El `fingerprint` se manda tal
   * como llegó en la fila: si el problema cambia, la fila vuelve a salir sola.
   * @param {{key: string, rowId: string, fingerprint: string, note?: string, days?: number}} body
   */
  snoozeAlert: async ({ key, rowId, fingerprint, note, days }) => {
    const { data } = await httpClient.post('/mgmt/alerts/ack/', {
      key, rowId, fingerprint, note, days,
    });
    return data;
  },

  /** Deshacer un «Contactado». */
  unsnoozeAlert: async ({ key, rowId }) => {
    await httpClient.delete('/mgmt/alerts/ack/', { data: { key, rowId } });
  },

  /**
   * Scoreboard de cuentas (Admin only): la cola comercial de la última noche.
   * @param {{lane?: 'ivan'|'founders'}} opts sin `lane` vienen los dos carriles.
   * @returns {Promise<{run: object|null, latestRun: object|null, rows: object[]}>}
   */
  scoreboard: async ({ lane } = {}) => {
    const { data } = await httpClient.get('/mgmt/scoreboard/', {
      params: lane ? { lane } : {},
    });
    return {
      run: data?.run || null,
      latestRun: data?.latestRun || null,
      rows: Array.isArray(data?.rows) ? data.rows : [],
    };
  },

  /** Encola el recálculo del scoreboard («Recalcular ahora»). 409 si ya hay uno. */
  recomputeScoreboard: async () => {
    const { data } = await httpClient.post('/mgmt/scoreboard/recompute/');
    return data;
  },

  /** List all sessions/users (Admin only). */
  users: async (params = {}) => {
    const { data } = await httpClient.get('/mgmt/users/', { params });
    return data;
  },

  /** Get details of a specific user (Admin only). */
  userDetail: async (userId) => {
    const { data } = await httpClient.get(`/mgmt/users/${userId}/`);
    return data;
  },

  /** Update user (Admin only). */
  updateUser: async (userId, fields) => {
    const { data } = await httpClient.patch(`/mgmt/users/${userId}/`, fields);
    return data;
  },

  /** Delete user (Admin only). */
  deleteUser: async (userId) => {
    const { data } = await httpClient.delete(`/mgmt/users/${userId}/`);
    return data;
  },

  /** Marca al usuario como favorito del equipo de admin y guarda/actualiza su
   * nota. Idempotente: repetirlo sobre un favorito ya marcado solo cambia la
   * nota. Devuelve el usuario serializado (mismo shape que el detalle). */
  setUserFavorite: async (userId, note = '') => {
    const { data } = await httpClient.put(`/mgmt/users/${userId}/favorite/`, { note });
    return data;
  },

  /** Quita la estrella y borra la nota. */
  unsetUserFavorite: async (userId) => {
    const { data } = await httpClient.delete(`/mgmt/users/${userId}/favorite/`);
    return data;
  },

  /* ── Dispositivos incluidos con el plan anual ───────────────────────────
   * La lista de envíos que el equipo tiene que despachar a mano. Cuelga de
   * `/mgmt/` como el resto del panel. */

  /** Una página de la lista. `params`: {q, status, page, pageSize, sort_by, sort_dir}. */
  includedDevices: async (params = {}) => {
    const { data } = await httpClient.get('/mgmt/included-devices/', { params });
    return data;
  },

  /** Alta manual — típicamente alguien que paga por fuera de la plataforma.
   * Un 409 significa «ya está en la lista» y trae la ficha existente en
   * `error.response.data.shipment`, para poder decir de quién es. */
  addIncludedDevices: async (payload) => {
    const { data } = await httpClient.post('/mgmt/included-devices/', payload);
    return data;
  },

  /** Cambia estado, nota y/o número de dispositivos de un envío. */
  updateIncludedDevices: async (shipmentId, payload) => {
    const { data } = await httpClient.patch(
      `/mgmt/included-devices/${shipmentId}/`, payload,
    );
    return data;
  },

  /** La ficha de un cliente concreto. Devuelve `{applies: false}` a secas
   * cuando no le toca — la tarjeta del detalle no se pinta. */
  userIncludedDevices: async (userId) => {
    const { data } = await httpClient.get(`/mgmt/users/${userId}/included-devices/`);
    return data;
  },

  /** Grant admin-assigned Pro to a user (no Stripe). */
  assignPro: async (userId, payload) => {
    const { data } = await httpClient.post(`/mgmt/users/${userId}/assign-pro/`, payload);
    return data;
  },

  /** Enlace de pago para un cliente free (`{seats, period, ctaId}`).
   *  Devuelve `{url, seats, period, netCents, grossCents, expiresAt}`. */
  createPaymentLink: async (userId, payload) => {
    const { data } = await httpClient.post(`/mgmt/users/${userId}/payment-link/`, payload);
    return data;
  },

  /** Promotion codes de Stripe que se pueden poner en un enlace de pago, con
   *  `usable`/`reason` para este cliente (`QR/billing/payment_links.py`). */
  getPaymentLinkPromoCodes: async (userId) => {
    const { data } = await httpClient.get('/mgmt/billing/promotion-codes/', {
      params: userId ? { userId } : {},
    });
    return data?.results || [];
  },

  /** Pasar de mensual a anual (campaña de closers, `QR/billing/annual_switch.py`).
   *  `getAnnualSwitch` → `{eligible, reason, seats, currentFamily, options}`. */
  getAnnualSwitch: async (userId) => {
    const { data } = await httpClient.get(`/mgmt/users/${userId}/annual-switch/`);
    return data;
  },

  /** Importe exacto que Stripe cobraría ahora (`{family}`). El `prorationDate`
   *  de la respuesta se reenvía al confirmar. */
  previewAnnualSwitch: async (userId, family) => {
    const { data } = await httpClient.post(`/mgmt/users/${userId}/annual-switch/preview/`, { family });
    return data;
  },

  /** Cobra el prorrateo y pasa a anual (`{family, prorationDate, amountPreviewedCents}`).
   *  Los errores llegan como `{code, message, detail}` con el detalle de Stripe. */
  switchToAnnual: async (userId, payload) => {
    const { data } = await httpClient.post(`/mgmt/users/${userId}/annual-switch/`, payload, {
      // El cobro con Stripe puede tardar más que los 15 s por defecto.
      timeout: 60000,
    });
    return data;
  },

  /** Bloquea al usuario: no entra, no se registra otra vez con su correo ni su
   * teléfono y deja de generar tareas (§162). `reason` es una nota interna. */
  blockUser: async (userId, reason) => {
    const { data } = await httpClient.post(`/mgmt/users/${userId}/block/`, { reason });
    return data;
  },

  /** Levanta el bloqueo. Solo existe mientras la cuenta no se haya borrado. */
  unblockUser: async (userId) => {
    const { data } = await httpClient.post(`/mgmt/users/${userId}/unblock/`);
    return data;
  },

  /** Borra la cuenta bloqueada y sus datos; el veto del correo y del teléfono
   * se queda. No es el DELETE de la ficha, que ni cascadea ni veta. */
  deleteBlockedUser: async (userId) => {
    const { data } = await httpClient.post(`/mgmt/users/${userId}/delete-blocked/`);
    return data;
  },

  /** Abre una sesión de soporte sobre la cuenta del cliente. El backend NO
   * cambia `request.user`: es un overlay de sesión, así que el admin sigue
   * siendo quien es y todo lo que haga queda auditado como soporte. */
  startImpersonation: async (userId) => {
    const { data } = await httpClient.post(`/mgmt/users/${userId}/impersonate/`);
    return data;
  },

  /** Vuelve a la cuenta del admin. Idempotente. */
  stopImpersonation: async () => {
    const { data } = await httpClient.post('/mgmt/impersonate/stop/');
    return data;
  },

  /** Alterna el modo fiel: ver la app como la ve el cliente, con su plan y sus
   * límites, en vez de con los superpoderes de admin. Devuelve el estado
   * completo de la sesión de soporte. 409 si no hay ninguna abierta. */
  setImpersonationViewMode: async (enabled) => {
    const { data } = await httpClient.post('/mgmt/impersonate/view-as/', { enabled });
    return data;
  },

  /** Revoke a prior admin-assigned Pro grant. */
  revokePro: async (userId) => {
    const { data } = await httpClient.post(`/mgmt/users/${userId}/revoke-pro/`);
    return data;
  },

  /** Relanza a mano el informe mensual del mes anterior para todos los locales
   * activos del usuario (más el agregado de marca si le corresponde).
   * Reemplaza los PDF existentes de ese periodo; el enlace de descarga del
   * cliente no cambia y no se le manda ningún correo.
   *
   * Devuelve 202 con `{year, month, period_label, rebuilt, created,
   * aggregated, skipped[]}` — el render es asíncrono, así que al volver
   * todavía no hay PDF nuevo. */
  /**
   * POST /mgmt/users/<id>/forgive-geogrid-debt/ — pone a cero un saldo de
   * créditos del Scanner SEO NEGATIVO.
   *
   * El saldo se queda negativo cuando alguien compra un pack, lo gasta y
   * revierte el pago: es lo que impide que eso salga gratis. Se perdona cuando
   * el castigo no corresponde — un banco que revierte un cargo por error.
   *
   * Idempotente: devuelve `{forgiven: 0}` si no había deuda.
   */
  forgiveGeogridDebt: async (userId) => {
    const { data } = await httpClient.post(
      `/mgmt/users/${userId}/forgive-geogrid-debt/`,
    );
    return data;
  },

  /**
   * Corrige a mano el saldo INCLUIDO de créditos del Scanner SEO.
   *
   * `delta` es la variación, no el saldo final: el apunte del libro mayor es una
   * variación, y así el servidor lo aplica con un `$inc` atómico en vez de
   * escribir un total que otro admin puede haber cambiado entre medias.
   *
   * Lo comprado no se toca: tiene una factura detrás.
   *
   * @param {string} userId
   * @param {{delta: number, note?: string}} payload
   * @returns {Promise<{adjusted: number, state: object}>}
   */
  adjustGeogridCredits: async (userId, { delta, note } = {}) => {
    const { data } = await httpClient.post(
      `/mgmt/users/${userId}/adjust-geogrid-credits/`,
      { delta, note: note || '' },
    );
    return data;
  },

  /**
   * Sube (o reemplaza) el logo del informe en un ÁMBITO.
   *
   * `scope` es `'all'` (el logo de toda la cuenta, y el valor por defecto),
   * `'brand'` (solo el informe de marca) o el id de un local con plan Business.
   * El servidor vuelve a comprobar que el local es Pro: el candado del selector
   * es la explicación, no la cerradura.
   *
   * NO se fija `Content-Type`: el navegador tiene que poner el suyo con el
   * boundary del multipart. Fijarlo a mano rompe el parseo en el servidor.
   */
  uploadReportLogo: async (userId, file, scope = 'all') => {
    const form = new FormData();
    form.append('file', file);
    form.append('scope', scope);
    const { data } = await httpClient.post(
      `/mgmt/users/${userId}/report-logo/`, 
      form,
      { headers: { 'Content-Type': undefined } }
    );
    return data;
  },

  deleteReportLogo: async (userId, scope = 'all') => {
    await httpClient.delete(
      `/mgmt/users/${userId}/report-logo/?scope=${encodeURIComponent(scope)}`,
    );
  },

  /**
   * URL de los bytes del logo de ESE ámbito, para la miniatura. Pasa por el
   * panel (admin).
   *
   * Da 404 cuando el ámbito no tiene logo propio, aunque la cuenta sí tenga uno:
   * la miniatura es lo único que distingue «este local tiene el suyo» de «hereda
   * el de la cuenta», y con cascada los pintaría iguales.
   */
  reportLogoRawUrl: (userId, version = '', scope = 'all') => {
    const params = new URLSearchParams({ scope });
    if (version) params.set('v', version);
    return `/api/v1/mgmt/users/${userId}/report-logo/raw/?${params.toString()}`;
  },

  /**
   * URL de la vista previa: una página real del informe, con este logo puesto.
   * Se mete en un iframe, de ahí que devuelva la URL y no el HTML.
   *
   * Aquí SÍ hay cascada, porque esto es lo que va a salir impreso: un local sin
   * logo propio se previsualiza con el de la cuenta, que es lo que llevará.
   */
  reportLogoPreviewUrl: (userId, { variant = 'monthly', version = '', scope = 'all' } = {}) => {
    const params = new URLSearchParams({ variant, scope });
    if (version) params.set('v', version);
    return `/api/v1/mgmt/users/${userId}/report-logo/preview/?${params.toString()}`;
  },

  rebuildMonthlyReports: async (userId) => {
    const { data } = await httpClient.post(
      `/mgmt/users/${userId}/rebuild-monthly-reports/`,
    );
    return data;
  },

  /** Read-only listing of the user's connected Google accounts + cached
   * locations (no Google API call, only Mongo cache). */
  getGbusinessConnections: async (userId) => {
    const { data } = await httpClient.get(`/mgmt/users/${userId}/gbusiness-connections/`);
    return data;
  },

  /** Le da a un local concreto el plan Business, dentro del cupo contratado
   * del usuario. 409 si no quedan asientos: para dar más está `assignPro`.
   * @param {string} userId
   * @param {string} locationId — id de Google, lleva barras (`locations/123`) */
  assignSeatToLocation: async (userId, locationId) => {
    const { data } = await httpClient.post(
      `/mgmt/users/${userId}/seated-locations/${locationId}/`,
    );
    return data;
  },

  /** Se lo quita. **Es la salida de soporte y la única que hay**: el cliente no
   * puede soltar un asiento —el backend rechaza cualquier lista que quite uno—
   * y su pantalla le manda aquí.
   * @param {string} userId
   * @param {string} locationId */
  releaseSeatFromLocation: async (userId, locationId) => {
    const { data } = await httpClient.delete(
      `/mgmt/users/${userId}/seated-locations/${locationId}/`,
    );
    return data;
  },

  /** Bloque A de la ficha: dispositivos, locales inferidos de sus place_id y
   * ranking de empleados. Puede pagar una llamada a Places por cada local que
   * todavía no tenga foto — ver `qr_app/services/places_snapshots.py`.
   * @param {string} userId */
  userDevicesOverview: async (userId) => {
    const { data } = await httpClient.get(`/mgmt/users/${userId}/devices-overview/`);
    return data;
  },

  /** Bloque B: solo aplica al usuario FREE que conectó Google Business. El
   * backend devuelve `{applies:false}` para el resto, así que el front no
   * reimplementa el predicado.
   * @param {string} userId */
  userGbpOverview: async (userId) => {
    const { data } = await httpClient.get(`/mgmt/users/${userId}/gbp-overview/`);
    return data;
  },

  /** Escaneos TERMINADOS del Mapa SEO del usuario, para el selector de
   * «Genera PDF» de su ficha.
   * @param {string} userId */
  userGeogridScans: async (userId) => {
    const { data } = await httpClient.get(`/mgmt/users/${userId}/geogrid-scans/`);
    return data?.results || [];
  },

  /** Informe PDF de un escaneo del Mapa SEO. Blob y no un enlace: la descarga
   * va por la sesión de admin. El render es síncrono en el servidor (Chromium,
   * unos segundos), de ahí el timeout propio en vez de los 15 s por defecto.
   * `comercial` es el slug del comercial que firma la última página (o
   * `ninguno` para un cierre sin tarjeta). `lang` es el idioma del informe:
   * `es` (por defecto) o `pt` (portugués de Portugal).
   * @param {string} scanId
   * @param {string} [comercial]
   * @param {'es'|'pt'} [lang] */
  downloadGeogridReport: async (scanId, comercial, lang = 'es') => {
    const params = { lang };
    if (comercial) params.comercial = comercial;
    const response = await httpClient.get(`/mgmt/geogrid-scans/${scanId}/report/`, {
      params,
      responseType: 'blob',
      timeout: 120000,
    });
    const disposition = response.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename="?([^";]+)"?/i);
    return { blob: response.data, filename: match ? match[1] : `mapa-seo-${scanId}.pdf` };
  },

  /** Bloques C/D/E: prueba en curso, baja programada o conversión a pago.
   * @param {string} userId */
  userTrialOverview: async (userId) => {
    const { data } = await httpClient.get(`/mgmt/users/${userId}/trial-overview/`);
    return data;
  },

  /** Vuelve a pedir a Google la ficha de estos locales. Es el ÚNICO camino que
   * gasta sobre un place_id que ya tenía foto: el GET nunca refresca.
   * @param {string} userId
   * @param {string[]} placeIds */
  refreshPlaceSnapshots: async (userId, placeIds) => {
    const { data } = await httpClient.post(
      `/mgmt/users/${userId}/places-refresh/`, { placeIds },
    );
    return data;
  },

  /** The user's tenant activity log (audit) for support. UN-masked: the real
   * Tapstar agent shows for support-authored events. Same shape the owner feed
   * uses, so <ActivityLogFeed> consumes it directly.
   * @param {string} userId
   * @param {{ page?: number, pageSize?: number, category?: string, from?: string, to?: string }} [opts]
   */
  getUserActivityLog: async (userId, { page = 1, pageSize = 25, category, from, to } = {}) => {
    const params = { page, page_size: pageSize };
    if (category) params.category = category;
    if (from) params.from = from;
    if (to) params.to = to;
    const { data } = await httpClient.get(`/mgmt/users/${userId}/activity/`, { params });
    return {
      results: Array.isArray(data?.results) ? data.results : [],
      page: data?.page || 1,
      pageSize: data?.pageSize || pageSize,
      total: data?.total || 0,
      categories: Array.isArray(data?.categories) ? data.categories : [],
    };
  },

  products: async (params = {}) => {
    const { data } = await httpClient.get('/mgmt/products/', { params });
    return data;
  },

  productDetail: async (productId) => {
    const { data } = await httpClient.get(`/mgmt/products/${productId}/`);
    return data;
  },

  updateProduct: async (productId, fields) => {
    const { data } = await httpClient.patch(`/mgmt/products/${productId}/`, fields);
    return data;
  },

  deleteProduct: async (productId) => {
    const { data } = await httpClient.delete(`/mgmt/products/${productId}/`);
    return data;
  },

  stats: async () => {
    const { data } = await httpClient.get('/mgmt/stats/');
    return data;
  },

  mapData: async () => {
    const { data } = await httpClient.get('/mgmt/map-data/');
    return data;
  },

  bulkHistory: async (params = {}) => {
    const { data } = await httpClient.get('/mgmt/bulk-products/', { params });
    return data;
  },

  createBulkProducts: async (payload) => {
    const { data } = await httpClient.post('/mgmt/bulk-products/', payload);
    return data;
  },

  updateBulkPlatform: async (historyId, payload) => {
    const { data } = await httpClient.patch(`/mgmt/bulk-products/${historyId}/platform/`, payload);
    return data;
  },

  gmb: async () => {
    const { data } = await httpClient.get('/mgmt/gmb/');
    return data;
  },

  getSettings: async () => {
    const { data } = await httpClient.get('/mgmt/settings/');
    return data;
  },

  updateSettings: async (payload) => {
    const { data } = await httpClient.patch('/mgmt/settings/', payload);
    return data;
  },

  getPublicSettings: async () => {
    const { data } = await httpClient.get('/mgmt/settings/public/');
    return data;
  },

  // ── Métricas (dirección de producto) ──────────────────────────────────────
  // `params` = { from, to, granularity, include_trialing }. `signal` lo pasa
  // react-query para abortar la petición cuando el rango cambia antes de que
  // vuelva. Libera la conexión del navegador; el servidor NO se entera, por eso
  // el backend lleva además su propio `maxTimeMS`.
  metricsOverview: async (params = {}, signal) => {
    const { data } = await httpClient.get('/mgmt/metrics/overview/', { params, signal });
    return data;
  },
  metricsGrowth: async (params = {}, signal) => {
    const { data } = await httpClient.get('/mgmt/metrics/growth/', { params, signal });
    return data;
  },
  metricsActivation: async (params = {}, signal) => {
    const { data } = await httpClient.get('/mgmt/metrics/activation/', { params, signal });
    return data;
  },
  metricsEngagement: async (params = {}, signal) => {
    const { data } = await httpClient.get('/mgmt/metrics/engagement/', { params, signal });
    return data;
  },
  metricsMonetization: async (params = {}, signal) => {
    const { data } = await httpClient.get('/mgmt/metrics/monetization/', { params, signal });
    return data;
  },
  metricsChurn: async (params = {}, signal) => {
    const { data } = await httpClient.get('/mgmt/metrics/churn/', { params, signal });
    return data;
  },
  metricsContent: async (params = {}, signal) => {
    const { data } = await httpClient.get('/mgmt/metrics/content/', { params, signal });
    return data;
  },
  /** Activaciones de widgets embebibles + lista de quién activó qué. */
  metricsWidgets: async (params = {}, signal) => {
    const { data } = await httpClient.get('/mgmt/metrics/widgets/', { params, signal });
    return data;
  },
  /** Ranking interno de closers (quién cerró cada venta). */
  metricsClosers: async (params = {}, signal) => {
    const { data } = await httpClient.get('/mgmt/metrics/closers/', { params, signal });
    return data;
  },
  /**
   * Economía del Scanner SEO: coste real, ingreso por packs, desperdicio y
   * salud contable de los monederos de créditos.
   */
  metricsGeogrid: async (params = {}, signal) => {
    const { data } = await httpClient.get('/mgmt/metrics/geogrid/', { params, signal });
    return data;
  },

  /**
   * Ficha de referencia de los créditos: qué es un crédito, qué cuesta cada
   * cuadrícula, qué se concede en cada hito, qué compra cada pack y qué acumula
   * cada perfil. Sin parámetros y sin periodo — todo sale DERIVADO de las
   * constantes que de verdad cobran y conceden, así que no puede desfasarse.
   */
  metricsGeogridReference: async () => {
    const { data } = await httpClient.get('/mgmt/metrics/geogrid/reference/');
    return data;
  },

  /**
   * Embudo del recorrido guiado: cuántos son elegibles, cuántos lo empiezan,
   * dónde se quedan y cuánto tardan en terminarlo.
   */
  metricsOnboarding: async (params = {}, signal) => {
    const { data } = await httpClient.get('/mgmt/metrics/onboarding/', { params, signal });
    return data;
  },

  /** Clicks/altas por CTA de marketing + ranking de conversión. */
  metricsCta: async (params = {}, signal) => {
    const { data } = await httpClient.get('/mgmt/metrics/cta/', { params });
    return data;
  },

  // ── Links de CTA de marketing (generador no-code) ─────────────────────────
  ctaLinks: async (params = {}) => {
    const { data } = await httpClient.get('/mgmt/cta-links/', { params });
    return data;
  },

  // Desde qué PANTALLA se pulsa un CTA. Va aparte del listado porque son
  // catorce filas de detalle por CTA que solo se miran de una en una.
  ctaPages: async (ctaId) => {
    const { data } = await httpClient.get(
      `/mgmt/cta-links/${encodeURIComponent(ctaId)}/pages/`,
    );
    return data;
  },

  // Qué BOTÓN cerró las altas que se llevó una campaña. Solo para links de
  // campaña: la vía de un CTA interno es él mismo (decisiones §136).
  ctaVia: async (ctaId) => {
    const { data } = await httpClient.get(
      `/mgmt/cta-links/${encodeURIComponent(ctaId)}/via/`,
    );
    return data;
  },

  // ── Export completo de usuarios (CSV en background) ─────────────────────
  // El POST responde 202 con el job; el CSV se genera en un worker por lotes
  // y la pantalla sondea el progreso hasta que se puede descargar.
  userExportLatest: async () => {
    const { data } = await httpClient.get('/mgmt/export/users/');
    return data;
  },

  startUserExport: async () => {
    const { data } = await httpClient.post('/mgmt/export/users/');
    return data;
  },

  userExportJob: async (jobId) => {
    const { data } = await httpClient.get(`/mgmt/export/users/${jobId}/`);
    return data;
  },

  // Blob y no un enlace directo: la descarga va por la sesión de admin.
  downloadUserExport: async (jobId) => {
    const response = await httpClient.get(`/mgmt/export/users/${jobId}/download/`, {
      responseType: 'blob',
      timeout: 0,
    });
    return response;
  },

  // ── Cuentas demo (decisiones §139) ──────────────────────────────────────
  // El POST responde 202 con el job, no con la cuenta: sembrarla son ~9.000
  // documentos y dos llamadas a OpenAI, asi que se encola y se sondea.
  demoAccounts: async () => {
    const { data } = await httpClient.get('/mgmt/demo-accounts/');
    return data;
  },

  createDemoAccount: async (payload) => {
    const { data } = await httpClient.post('/mgmt/demo-accounts/', payload);
    return data;
  },

  demoSeedJob: async (jobId) => {
    const { data } = await httpClient.get(`/mgmt/demo-accounts/jobs/${jobId}/`);
    return data;
  },

  deleteDemoAccount: async (userId) => {
    const { data } = await httpClient.delete(`/mgmt/demo-accounts/${userId}/`);
    return data;
  },

  // Síncrono (201), no un job: sin IA y un par de segundos. Cada llamada AÑADE.
  addDemoAccountDevices: async (userId, payload) => {
    const { data } = await httpClient.post(`/mgmt/demo-accounts/${userId}/devices/`, payload);
    return data;
  },

  // ── Correo «Tu informe de Google Maps» (Admin → Informe Maps) ───────────
  informeMapa: async () => {
    const { data } = await httpClient.get('/mgmt/informe-mapa/');
    return data;
  },

  informeMapaRecuento: async (payload) => {
    const { data } = await httpClient.post('/mgmt/informe-mapa/recuento/', payload);
    return data;
  },

  // 202 = encolado. 409 trae `enviables` si la audiencia cambió desde el recuento.
  informeMapaEnviar: async (payload) => {
    const { data } = await httpClient.post('/mgmt/informe-mapa/enviar/', payload);
    return data;
  },

  // ── Admin → Place IDs: a qué apunta cada place_id (negocio, calle…) ────
  // Recontar contesta 202 (lotes en Celery) o 409 si ya hay una pasada en marcha;
  // el progreso se sondea con `placeIds()`.
  placeIds: async (params) => {
    const { data } = await httpClient.get('/mgmt/place-ids/', { params });
    return data;
  },
  // Todos los dispositivos de un place_id: la tabla solo trae los 25 primeros.
  placeIdDispositivos: async (placeId) => {
    const { data } = await httpClient.get(
      `/mgmt/place-ids/${encodeURIComponent(placeId)}/dispositivos/`,
    );
    return data;
  },
  placeIdsRecontar: async (payload) => {
    const { data } = await httpClient.post('/mgmt/place-ids/recontar/', payload);
    return data;
  },
  placeIdsPausar: async () => {
    const { data } = await httpClient.post('/mgmt/place-ids/pausar/');
    return data;
  },

  // ── Campañas comerciales (Admin → Campaña Mapa SEO / Mapa Google Business) ──
  // Recontar y preparar contestan 202: el trabajo va en Celery y el estado se
  // sondea con `prospeccion()`. Preparar y enviar devuelven 409 si la cifra
  // confirmada ya no es la de ahora. `base` es la ruta de la campaña
  // (`PROSPECCION_BASE` o `/mgmt/prospeccion/google-business`; ver
  // `components/admin/prospeccion/prospeccionUi.jsx`, `CAMPANAS`).
  prospeccion: async (base = PROSPECCION_BASE) => {
    const { data } = await httpClient.get(`${base}/`);
    return data;
  },
  prospeccionRecuento: async (payload, base = PROSPECCION_BASE) => {
    const { data } = await httpClient.post(`${base}/recuento/`, payload);
    return data;
  },
  prospeccionPreparar: async (payload, base = PROSPECCION_BASE) => {
    const { data } = await httpClient.post(`${base}/preparar/`, payload);
    return data;
  },
  prospeccionEnvios: async (params, base = PROSPECCION_BASE) => {
    const { data } = await httpClient.get(`${base}/envios/`, { params });
    return data;
  },
  /** Métricas por tanda y comercial, más las del flujo de Loops. */
  prospeccionMetricas: async (base = PROSPECCION_BASE) => {
    const { data } = await httpClient.get(`${base}/metricas/`);
    return data;
  },
  /** El PDF de un registro como blob (va por la sesión de admin). */
  prospeccionPdf: async (envioId) => {
    const response = await httpClient.get(`/mgmt/prospeccion/envios/${envioId}/pdf/`, {
      responseType: 'blob',
      timeout: 60000,
    });
    return response.data;
  },
  /** Los registros de campaña de un usuario (ficha de admin). */
  prospeccionUsuario: async (userId) => {
    const { data } = await httpClient.get(`/mgmt/prospeccion/usuarios/${userId}/`);
    return data?.results || [];
  },
  // Admin → Campaña pt (QR/prospeccion/views_oferta_pt.py): el correo del
  // Plano Google Maps. `accion` es prueba | recuento | preparar | enviar.
  campanaPt: async () => {
    const { data } = await httpClient.get('/mgmt/prospeccion/oferta-pt/');
    return data;
  },
  // `filtro`: un estado o una señal (con_ficha, sin_ficha, abrio, entro,
  // baja_loops, prueba); `q` busca en el correo.
  campanaPtMetricas: async () => {
    const { data } = await httpClient.get('/mgmt/prospeccion/oferta-pt/metricas/');
    return data;
  },
  campanaPtEnvios: async (params) => {
    const { data } = await httpClient.get('/mgmt/prospeccion/oferta-pt/envios/', { params });
    return data;
  },
  // De quién es un enlace /oferta/<token> pegado de PostHog. POST: el token
  // abre sesión y en la query string quedaría en los logs.
  campanaPtEnlace: async (enlace) => {
    const { data } = await httpClient.post('/mgmt/prospeccion/oferta-pt/enlace/', { enlace });
    return data;
  },
  campanaPtAccion: async (accion, payload) => {
    const { data } = await httpClient.post(`/mgmt/prospeccion/oferta-pt/${accion}/`, payload);
    return data;
  },
  prospeccionAccion: async (accion, payload, base = PROSPECCION_BASE) => {
    const { data } = await httpClient.post(`${base}/${accion}/`, payload);
    return data;
  },
  // Saca a alguien del seguimiento (agendó, respondió, no le interesa).
  prospeccionPararSeguimiento: async (envioId, motivo, base = PROSPECCION_BASE) => {
    const { data } = await httpClient.post(
      `${base}/envios/${envioId}/parar-seguimiento/`, { motivo });
    return data;
  },
  // Pulsar «reservar» no dice si reservó: el comercial lo confirma (POST) o lo
  // quita si fue un clic por error (DELETE). Confirmarla para el seguimiento.
  prospeccionConfirmarReserva: async (envioId, base = PROSPECCION_BASE) => {
    const { data } = await httpClient.post(`${base}/envios/${envioId}/reserva-confirmada/`);
    return data;
  },
  prospeccionQuitarReserva: async (envioId, base = PROSPECCION_BASE) => {
    const { data } = await httpClient.delete(`${base}/envios/${envioId}/reserva-confirmada/`);
    return data;
  },
  // El paso siguiente a la reserva confirmada: el comercial cerró la venta. El
  // servidor pasa además el lead a «ganado» (y lo devuelve al quitarlo).
  prospeccionCerrar: async (envioId, base = PROSPECCION_BASE) => {
    const { data } = await httpClient.post(`${base}/envios/${envioId}/cierre/`);
    return data;
  },
  prospeccionQuitarCierre: async (envioId, base = PROSPECCION_BASE) => {
    const { data } = await httpClient.delete(`${base}/envios/${envioId}/cierre/`);
    return data;
  },

  createCtaLink: async (payload) => {
    const { data } = await httpClient.post('/mgmt/cta-links/', payload);
    return data;
  },

  updateCtaLink: async (ctaId, payload) => {
    const { data } = await httpClient.patch(`/mgmt/cta-links/${ctaId}/`, payload);
    return data;
  },

};

export const tripadvisorService = {
  /** Search TripAdvisor locations by query. */
  search: async (query) => {
    const { data } = await httpClient.get('/tripadvisor/search/', { params: { q: query } });
    return data;
  },

  /** Get details (name, address, rating, write_review URL, etc.) for a location. */
  details: async (locationId) => {
    const { data } = await httpClient.get(`/tripadvisor/details/${locationId}/`);
    return data;
  },

  /** Get photos for a TripAdvisor location. */
  photos: async (locationId) => {
    const { data } = await httpClient.get(`/tripadvisor/photos/${locationId}/`);
    return data;
  },
};
